import React, { useState, useRef } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { publicRequest } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { GoogleReCaptcha, googleReCaptchaValidation } from './GoogleReCaptcha'
import { isValidEmail, isValidPassword } from './_helpers/validation'
import CustomTextField from './_form_fields/CustomTextField'
import CustomBackdrop from './CustomBackdrop'

const SignUpWrapper = (props) => {
  const { setLoginDialogOpen, setSignUpDialogOpen, handleNotificationDialogOpen } = props
  const theme = useTheme()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [captchaValidated, setCaptchaValidated] = useState(false)
  const [errorCaptcha, setErrorCaptcha] = useState(false)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const captchaRef = useRef('')

  // const sectionLabelStyle = {
  //   fontSize: '18px',
  //   color: 'white',
  //   textTransform: 'uppercase',
  //   padding: '12px 0px'
  // }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const formControlStyle = {
    background: theme.palette.azureishWhite,
    color: theme.palette.violetsAreBlue,
    borderRadius: '30px',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
  }

  const outlinedInputErrorStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
  }

  const loginErrorHelperStyle = {
    color: theme.palette.congoPink,
    paddingLeft: '20px',
  }

  const loginHelperStyle = {
    color: 'white',
    paddingLeft: '20px',
  }

  const loginButtonStyle = {
    textTransform: 'capitalize',
    fontWeight: '400',
    color: theme.palette.aliceBlue,
    fontSize: '18px',
    textDecoration: 'underline',

    '&:hover': {
      background: 'inherit',
      textDecoration: 'none',
    },
  }

  const submitButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.violetsAreBlue,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.violetsAreBlue, 0.8),
    },
  }

  const handleChangeEmail = (e) => {
    const value = e.target.value
    setEmail(value)
    if (isValidEmail(value)) {
      setErrors(prev => {return { ...prev, email: null }})
    }
  }

  const handleChangePassword = (e) => {
    const value = e.target.value
    setPassword(value)
    if (isValidPassword(value)) {
      setErrors(prev => {return { ...prev, password: null }})
    }    
  }

  const handleChangeConfirmPassword = (e) => {
    const value = e.target.value
    setConfirmPassword(value)
    if (value === password) {
      setErrors(prev => {return { ...prev, confirmPassword: null }})
    }    
  }

  const handleChangeFirstName = (e) => {
    const value = e.target.value
    setFirstName(value)
    if (value) {
      setErrors(prev => {return { ...prev, firstName: null }})
    }    
  }

  const handleChangeLastName = (e) => {
    const value = e.target.value
    setLastName(value)
    if (value) {
      setErrors(prev => {return { ...prev, lastName: null }})
    }    
  }

  const scrollToTop = () => {
    const element = document.getElementById('formStart')
    element.scrollIntoView({ behavior: 'smooth' })
  }

  const handleClickLogin = () => {
    setSignUpDialogOpen(false)
    setLoginDialogOpen(true)
  }

  const signUpRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + clientAccountsURLs.getSignUp
      
    const request_data = { url, method: 'POST', data: postData }
    
    const response = await publicRequest(request_data)
    
    if (response && response.status_code && response.status_code < 400 && response.data && response.data.status && response.data.status === 'created') {
      setSignUpDialogOpen(false)
      handleNotificationDialogOpen({
        title: defaultTexts.signUp,
        text: defaultTexts.signUpSuccessfullText,
      })
    } else if (response && response.error && response.error.includes('error:email_exists')) {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.anErrorOccured,
          email: defaultTexts.emailAlreadyExists.replace('###EMAIL###', email),
        }
      })
    } else {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.anErrorOccured,
        }
      })
    }
  }

  const handleClickSubmit = async () => {
    setIsLoading(true)
    let _errors = {}

    if (!email) {
      _errors.email = defaultTexts.pleaseFillOut
    } else if (!isValidEmail(email)) {
      _errors.email = defaultTexts.invalidEmail
    }

    if (!password) {
      _errors.password = defaultTexts.pleaseFillOut
    } else if (!isValidPassword(password)) {
      _errors.password = defaultTexts.passwordRequirements
    }

    if (!confirmPassword) {
      _errors.confirmPassword = defaultTexts.pleaseFillOut
    } else if (confirmPassword !== password) {
      _errors.confirmPassword = defaultTexts.passwordDoesNotMatch
    }

    if (!firstName) {
      _errors.firstName = defaultTexts.pleaseFillOut
    }

    if (!lastName) {
      _errors.lastName = defaultTexts.pleaseFillOut
    }

    const captchaResponse = captchaValidated ? true : await googleReCaptchaValidation(captchaRef.current.getValue())
    setCaptchaValidated(captchaResponse ? true : false)

    if (Object.keys(_errors).length === 0 && captchaResponse) {
      const postData = {
        email,
        password,
        confirm_password: confirmPassword,
        firstName,
        lastName,
      }
      signUpRequest(postData)
    }
    
    if (Object.keys(_errors).length > 0) {
      scrollToTop()
      _errors.generalError = defaultTexts.anErrorOccured
      setErrors(_errors)
    }
    
    if (!captchaResponse) {
      setErrorCaptcha(true)
    }

    setIsLoading(false)
  }

  return (
    <Box id='formStart'>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      {
        errors.generalError &&
        <Alert severity='error' sx={{ fontSize: '16px' }}>{errors.generalError}</Alert>
      }

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='filled'
          label={defaultTexts.email}
          currentValue={email}
          setCurrentValue={handleChangeEmail}
          required={true}
          loginError={errors.email ? true : false}
          errorText={errors.email}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='filled'
          label={defaultTexts.password}
          currentValue={password}
          setCurrentValue={handleChangePassword}
          required={true}
          loginError={errors.password ? true : false}
          loginHelperText={defaultTexts.passwordRequirements}
          loginErrorSx={outlinedInputErrorStyle}
          loginHelperSx={errors.password ? loginErrorHelperStyle : loginHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='filled'
          label={defaultTexts.confirmPassword}
          currentValue={confirmPassword}
          setCurrentValue={handleChangeConfirmPassword}
          required={true}
          loginError={errors.confirmPassword ? true : false}
          errorText={errors.confirmPassword}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='filled'
          label={defaultTexts.firstName}
          currentValue={firstName}
          setCurrentValue={handleChangeFirstName}
          required={true}
          loginError={errors.firstName ? true : false}
          errorText={errors.firstName}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='filled'
          label={defaultTexts.lastName}
          currentValue={lastName}
          setCurrentValue={handleChangeLastName}
          required={true}
          loginError={errors.lastName ? true : false}
          errorText={errors.lastName}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={{ padding: '12px 0px 32px 0px' }}>
        <center>
          <GoogleReCaptcha
            ref={captchaRef}
            error={errorCaptcha}
            errorColor={theme.palette.congoPink}
            errorCenter={true}
          />
        </center>
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems='center'
        justifyContent={{ xs: 'flex-start', md: 'space-between' }}
        spacing={1}
        sx={{ marginTop: '24px' }}
      >
        <Box>
          <Button
            variant='text'
            disableRipple
            disableElevation
            sx={loginButtonStyle}
            onClick={handleClickLogin}
          >
            {defaultTexts.login}
          </Button>
        </Box>
        <Box>
          <Button
            variant='text'
            disableRipple
            disableElevation
            sx={submitButtonStyle}
            onClick={handleClickSubmit}
          >
            {defaultTexts.submit}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}

export default SignUpWrapper